import {
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Button as MuiButton,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle,
  Edit,
  Lock,
  Mail,
  Phone,
  Shield,
  Unlock,
  User,
  XCircle
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import adminApi from "../../api/admin.js";
import ProfessionalVerificationModal from "../../components/admin/ProfessionalVerificationModal.jsx";
import UserManagementModal from "../../components/admin/UserManagementModal.jsx";
import { VerificationConfirmDialog } from "../../components/admin/VerificationConfirmationDialog.js";
import Button from "../../components/common/Button.jsx";
import { getDisplayAvatarChar } from "../../utils/helpers.js";
import AccountActivity from "./AccountActivity.jsx";
import CompanyDetailsTab from "./CompanyDetailsTab.jsx";
import PersonalInfoTab from "./PersonalInfoTab.jsx";
import SecurityTab from "./SecurityTab.jsx";
import UserDocumentsTab from "./UserDocumentsTab.jsx";

const tabs: { label: string; value: string; role: 'all' | 'recruiter' | 'professional'}[] = [
  {
    label: 'Personal Information',
    value: 'personal_info',
    role: 'all',
  },
  {
    label: 'Account Activity',
    value: 'acc_activity',
    role: 'all',
  },
  {
    label: 'Company Details',
    value: 'company_details',
    role: 'recruiter',
  },
  {
    label: 'Security',
    value: 'security',
    role: 'all'
  },
  {
    label: 'Documents',
    value: 'documents',
    role: 'professional'
  },
]

const AdminUserDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const queryClient = useQueryClient();
  const [recruiterVerificationModalOpen, setRecruiterVerificationModalOpen] = useState(false)

  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<User>({
    queryKey: ["user-details", id],
    queryFn: async () => {
      const res = await adminApi.getUserDetails(id);
      return res.data;
    },
    enabled: !!id,
  });

  const {
    data: professional,
    isLoading: professionalIsLoading,
    isError: professionalIsError,
    error: professionalError,
    refetch: professionalRefetch,
  } = useQuery<ProfessionalProfile>({
    queryKey: ["verify-professional", id],
    queryFn: async () => {
      const res = await adminApi.professionalVerificationStatus(id);
      return res.data;
    },
    enabled: !!id,
  });

  console.log("Professional Data:", professional);


  useEffect(() => {
  setTabValue(0)
}, [user?.role])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const visibleTabs = tabs.filter(
  (tab) => tab.role === 'all' || tab.role === user?.role
)


  const handleStatusToggle = async () => {
    setIsActivating(true);
    try {
      const res = await adminApi.activateOrDeactivateUser(id, !user?.is_active);
      await refetch();
      setStatusDialogOpen(false);
      toast.success(res.data?.message || 'User status updated successfully!')
    } catch (error) {
      console.error("Failed to update user status:", error);
      toast.error("Failed to update user status")
    } finally {
      setIsActivating(false);
    }
  };

  // Mutation for updating an existing user
  const updateUserMutation = useMutation<User, Error, {id: number, userData:User}>({
    mutationFn: ({ id, userData }) => adminApi.updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-details', id] });
      setUserModalOpen(false);
      toast.success('User updated successfully!');
      refetch();
    },
    onError: (error) => {
      console.error('Failed to update user:', error);
      if (isAxiosError(error)){
        toast.error(error.response?.data?.message || 'Failed to update user');
        setErrors(error.response?.data)
        return
      }
      toast.error('Something went wrong while updating user')
    }
  });

  const patchUserMutation = useMutation<User, Error, {id: number, patchData: {company_verification_status: 'verified' | 'unverified'}}>({
    mutationFn: ({ id, patchData: userData }) => adminApi.patchUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-details', id] });
      setUserModalOpen(false);
      toast.success('User updated successfully!');
      refetch();
    },
    onError: (error) => {
      console.error('Failed to update user:', error);
      if (isAxiosError(error)){
        toast.error(error.response?.data?.message || 'Failed to update user');
        setErrors(error.response?.data)
        return
      }
      toast.error('Something went wrong while updating user')
    }
  });

  

  // Update the verifyProfessionalMutation
  const verifyProfessionalMutation = useMutation<User, Error, {id:number; verificationData: {}}>({
    mutationFn: ({ id, verificationData }) => adminApi.verifyProfessional(id, verificationData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-details', id] });
      queryClient.invalidateQueries({ queryKey: ['verify-professional', id] });
      setVerificationModalOpen(false);
      toast.success('Professional verified successfully!');
      refetch();
      professionalRefetch();
    },
    onError: (error) => {
      console.error('Failed to verify professional:', error);
      toast.error(isAxiosError(error) ? error.response?.data?.message : 'Failed to verify professional');
    }
  });

  // Add this handler
  const handleVerificationSubmit = (verificationData) => {
    verifyProfessionalMutation.mutate({ 
      id: user!.id, 
      verificationData 
    });
  };



   const handleUserSubmit = (formData: User) => {
    if (user?.id) {
      updateUserMutation.mutate({ 
        id: user.id, 
        userData: formData 
      });
    }
  };

  const handlePatchUser = (verificationData: 'verified' | 'unverified') => {
    if (user?.id){
      patchUserMutation.mutate({
        id: user.id,
        patchData: {
          company_verification_status: verificationData
        }
      })
    }
  }

  const handleConfirm = () => {
  handlePatchUser(isCompanyProfileVerified ? 'unverified' : 'verified')
  setRecruiterVerificationModalOpen(false)
}

  const getDaysSinceJoined = () => {
    if (!user?.date_joined) return 0;
    const joined = new Date(user.date_joined);
    const now = new Date();
    return Math.floor((now - joined) / (1000 * 60 * 60 * 24));
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="h-8 bg-gray-200 rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            Error loading user details: {error?.message}
          </p>
          <Button
            onClick={() => navigate("/admin/users")}
            variant="outline"
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">User not found</p>
          <Button
            onClick={() => navigate("/admin/users")}
            variant="outline"
            className="mt-4"
            icon={<ArrowLeft className="w-4 h-4 mr-2" />}
          >
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  const isCompanyProfileVerified = user.company_verification_status == 'verified'

  const recruiterStats = [
    {
      label: "Company Profile",
      value: isCompanyProfileVerified ? "Verified" : "Unverified",
      icon: User,
      color: isCompanyProfileVerified ? "text-green-600" : "text-yellow-600",
      bgColor: isCompanyProfileVerified ? "bg-green-50" : "bg-yellow-50",
    },
  ]

  const professionalStats = [
    {
      label: "User Profile",
      value: professional?.verification_status.is_verified ? "Approved" : "Not Approved",
      icon: User,
      color: professional?.verification_status.is_verified ? "text-green-600" : "text-red-600",
      bgColor: professional?.verification_status.is_verified ? "bg-green-50" : "bg-red-50",
    },
  ]

  const userStats = [
    {
      label: "Account Status",
      value: user.is_active ? "Active" : "Inactive",
      icon: user.is_active ? CheckCircle : XCircle,
      color: user.is_active ? "text-green-600" : "text-red-600",
      bgColor: user.is_active ? "bg-green-50" : "bg-red-50",
    },
    {
      label: "Email Verified",
      value: user.is_email_verified ? "Verified" : "Unverified",
      icon: user.is_email_verified ? Shield : Mail,
      color: user.is_email_verified ? "text-green-600" : "text-yellow-600",
      bgColor: user.is_email_verified ? "bg-green-50" : "bg-yellow-50",
    },
    {
      label: "Member For",
      value: `${getDaysSinceJoined()} days`,
      icon: Calendar,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      label: "User Role",
      value: user.role.charAt(0).toUpperCase() + user.role.slice(1),
      icon: User,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  const finalStats = user.role == 'recruiter' ? [...userStats, ...recruiterStats] : [...userStats, ...professionalStats]

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users")}
            icon={<ArrowLeft className="w-4 h-4" />}
            className="p-2"
          />
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">
              User Details
            </h1>
            <p className="text-secondary-600">
              Manage user account and information
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => setStatusDialogOpen(true)}
            icon={
              user.is_active ? (
                <Lock className="w-4 h-4 mr-2" />
              ) : (
                <Unlock className="w-4 h-4 mr-2" />
              )
            }
          >
            {user.is_active ? "Deactivate" : "Activate"}
          </Button>
          <Button variant="outline" icon={<Edit className="w-4 h-4 mr-2" />} onClick={() => setUserModalOpen(true)}>
            Edit User
          </Button>
          {
            user.role === 'professional' &&
            <Button variant="outline" icon={<Check className="w-4 h-4 mr-2" />} onClick={() => setVerificationModalOpen(true)}>
            Verify Professional
          </Button>
          }
          {
            user.role === 'recruiter' &&
            <Button variant="outline" icon={<Check className="w-4 h-4 mr-2" />} onClick={() => setRecruiterVerificationModalOpen(true)}>
            {isCompanyProfileVerified ? "Disapprove" : "Approve" } Recruiter
          </Button>
          }
        </div>
      </div>

      {/* User Overview */}
      <Grid container spacing={3} className="mb-6">
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between lg:gap-2 mb-6">
                <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                  <div className="hidden w-16 h-16 overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 rounded-full sm:flex items-center justify-center">
                    {/* <User className="w-8 h-8 text-white" /> */}
                    <img
                      src={
                        user?.profile_picture ||
                        user?.company_logo ||
                        `https://placehold.co/100x100/E2E8F0/475569?text=${getDisplayAvatarChar(
                          user
                        )}`
                      }
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover shadow-sm"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-secondary-900">
                      {user.full_name || user.email}
                    </h2>
                    <p className="text-secondary-600 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {user.email}
                    </p>
                    {user.phone_number && (
                      <p className="text-secondary-600 flex items-center mt-1">
                        <Phone className="w-4 h-4 mr-2" />
                        {user.phone_number}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Chip
                    label={user.is_active ? "Active" : "Inactive"}
                    color={user.is_active ? "success" : "default"}
                    icon={
                      user.is_active ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )
                    }
                  />
                  <Chip
                    label={user.is_email_verified ? "Verified" : "Unverified"}
                    color={user.is_email_verified ? "success" : "warning"}
                    variant="outlined"
                  />
                  <Chip label={user.role} color="primary" variant="outlined" />
                </div>
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-4">
                <Typography
                  variant="subtitle2"
                  className="mb-3 text-secondary-600"
                >
                  Quick Actions:
                </Typography>
                <div className="flex flex-wrap gap-2">
                  {/* <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<Mail />}
                    onClick={() => window.location.href = `mailto:${user.email}`}
                  >
                    Send Email
                  </MuiButton> */}
                  <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => setUserModalOpen(true)}
                  >
                    Edit Profile
                  </MuiButton>
                  {/* <MuiButton
                    variant="outlined"
                    size="small"
                    startIcon={<Shield />}
                  >
                    Reset Password
                  </MuiButton> */}
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-secondary-900">
                Account Stats
              </h3>
              <div className="space-y-4">
                {finalStats.map((stat, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <div className={`p-2 rounded-lg ${stat.bgColor} mr-3`}>
                        <stat.icon className={`w-4 h-4 ${stat.color}`} />
                      </div>
                      <span className="text-secondary-600 text-sm">
                        {stat.label}
                      </span>
                    </div>
                    <span className={`font-medium ${stat.color}`}>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs Content */}
      {/* <Card>
        <div className="overflow-x-auto scrollbar-thin">
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            className="border-b"
            sx={{ minWidth: "max-content" }}
          >
            <Tab label="Personal Information" onClick={} />
            {user.role === 'recruiter' &&<Tab label="Company Details" />}
            <Tab label="Account Activity" />
            <Tab label="Security" />
            {user.role === 'professional' && <Tab label="Documents" />}
          </Tabs>
        </div>

        <CardContent className="p-6">
          {tabValue === 0 && (
            <PersonalInfoTab user={user} />
          )}

          {tabValue === 1 && user.role === 'recruiter' && (
            <CompanyDetailsTab user={user} />
          )}

          {tabValue === 2 && (
            <AccountActivity setStatusDialogOpen={setStatusDialogOpen} user={user} getDaysSinceJoined={getDaysSinceJoined} />
          )}

          {tabValue === 3 && (
            <SecurityTab user={user} />
          )}
          {tabValue === 4 && user.role === 'professional' && (
            <UserDocumentsTab refetchDocuments={professionalRefetch} professional={professional} professionalIsLoading={professionalIsLoading} userId={user.id} />
          )}
        </CardContent>
      </Card> */}
      <Card>
  <div className="overflow-x-auto scrollbar-thin">
    <Tabs
      value={tabValue}
      onChange={handleTabChange}
      className="border-b"
      sx={{ minWidth: 'max-content' }}
    >
      {visibleTabs.map((tab) => (
        <Tab key={tab.value} label={tab.label} />
      ))}
    </Tabs>
  </div>

  <CardContent className="p-6">
    {visibleTabs.map((tab, index) => {
      if (tabValue !== index) return null

      switch (tab.value) {
        case 'personal_info':
          return <PersonalInfoTab key={tab.value} user={user} />

        case 'company_details':
          return <CompanyDetailsTab key={tab.value} user={user} />

        case 'acc_activity':
          return (
            <AccountActivity
              key={tab.value}
              setStatusDialogOpen={setStatusDialogOpen}
              user={user}
              getDaysSinceJoined={getDaysSinceJoined}
            />
          )

        case 'security':
          return <SecurityTab key={tab.value} user={user} />

        case 'documents':
          return (
            <UserDocumentsTab
              key={tab.value}
              refetchDocuments={professionalRefetch}
              professional={professional}
              professionalIsLoading={professionalIsLoading}
              userId={user.id}
            />
          )

        default:
          return null
      }
    })}
  </CardContent>
</Card>

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>
          {user.is_active ? "Deactivate User" : "Activate User"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {user.is_active ? "deactivate" : "activate"} the account for{" "}
            <strong>{user.full_name || user.email}</strong>?
          </Typography>
          {user.is_active && (
            <Typography variant="body2" className="mt-2 text-yellow-600">
              Deactivating will prevent this user from accessing their account.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => setStatusDialogOpen(false)}
            disabled={isActivating}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleStatusToggle}
            variant="contained"
            color={user.is_active ? "error" : "success"}
            disabled={isActivating}
          >
            {isActivating
              ? "Updating..."
              : user.is_active
              ? "Deactivate"
              : "Activate"}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <UserManagementModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSubmit={handleUserSubmit}
        initialData={user}
        isLoading={updateUserMutation.isPending}
        title={user ? "Edit User" : "Create User"}
        submitButtonText={user ? "Update User" : "Create User"}
        errors={errors}
        setErrors={setErrors}
      />

      <ProfessionalVerificationModal
    open={verificationModalOpen}
    onClose={() => setVerificationModalOpen(false)}
    onSubmit={handleVerificationSubmit}
    initialData={professional?.verification_status}
    professionalData={professional}
    isLoading={verifyProfessionalMutation.isPending}
    title={`Verify ${user.full_name || user.email}`}
    submitButtonText="Update Verification"
  />

  <VerificationConfirmDialog
  open={recruiterVerificationModalOpen}
  onClose={() => setRecruiterVerificationModalOpen(false)}
  onConfirm={handleConfirm}
  isCurrentlyVerified={isCompanyProfileVerified}
  user={{ name: user?.full_name, email: user?.email }}
  isLoading={patchUserMutation.isPending}
/>
  
    </div>
  );
};

export default AdminUserDetailsPage;
